<script setup lang="ts">
import { getPackQuantityParts } from '~/utils/products'

const props = withDefaults(defineProps<{
  quantity: number
  typeCatName?: string | null
  norm?: number
  size?: 'default' | 'compact' | 'large'
  blockedLabel?: string | null
  emptyAdd?: boolean
  addLabel?: string | null
}>(), {
  typeCatName: null,
  norm: 1,
  size: 'default',
  blockedLabel: null,
  emptyAdd: false,
  addLabel: null,
})

const { t, locale } = useI18n()

const emit = defineEmits<{
  increase: []
  decrease: []
  remove: []
}>()

const display = computed(() =>
  getPackQuantityParts(props.quantity, props.typeCatName, props.norm, locale.value),
)

const isAddBlocked = computed(() => !!props.blockedLabel?.trim())

const showBlockedPill = computed(() =>
  isAddBlocked.value && props.quantity === 0,
)

const showEmptyAdd = computed(() =>
  props.emptyAdd && props.quantity === 0 && !isAddBlocked.value,
)

const addCtaLabel = computed(() => props.addLabel || t('product.addToCart'))
</script>

<template>
  <div
    v-if="showBlockedPill"
    class="qty-controls qty-blocked-pill"
    :class="`qty-controls--${size}`"
  >
    {{ blockedLabel }}
  </div>
  <button
    v-else-if="showEmptyAdd"
    type="button"
    class="qty-controls qty-add-cta"
    :class="`qty-controls--${size}`"
    @click.prevent="emit('increase')"
  >
    <span class="qty-add-cta-icon" aria-hidden="true" />
    <span>{{ addCtaLabel }}</span>
  </button>
  <div
    v-else
    class="qty-controls"
    :class="`qty-controls--${size}`"
  >
    <button
      v-if="quantity <= 1"
      type="button"
      class="qty-btn"
      :class="{
        bin: quantity === 1,
        'qty-btn--inactive': quantity === 0,
      }"
      :disabled="quantity < 1"
      @click.prevent="emit('remove')"
    >
      <Icon
        v-if="quantity === 1"
        name="tabler:trash"
      />
      <template v-else>−</template>
    </button>
    <button
      v-if="quantity > 1"
      type="button"
      class="qty-btn"
      @click.prevent="emit('decrease')"
    >−</button>
    <label class="qty-value">
      <span>{{ display.qty }}</span>
      <span v-if="display.unit" class="qty-value-unit">{{ display.unit }}</span>
    </label>
    <button
      type="button"
      class="qty-btn add"
      :disabled="isAddBlocked"
      @click.prevent="emit('increase')"
    >+</button>
  </div>
</template>

<style scoped>
/* Size variants only override these tokens — never the rules below. */
.qty-controls {
  --qty-size: 42px;
  --qty-gap: 0.6rem;
  --qty-btn-font: 1.875rem;
  --qty-value-font: 1.5rem;
  --qty-radius: 30px;
  --qty-icon-size: 1.5rem;
  --qty-pill-width: 215px;
  --qty-pill-padding: 0 12px;
  --qty-pill-font: 0.875rem;

  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  flex-shrink: 0;
  gap: var(--qty-gap);
}

.qty-controls--compact {
  --qty-size: 28px;
  --qty-gap: 0.4rem;
  --qty-btn-font: 1.25rem;
  --qty-value-font: 1rem;
  --qty-radius: 20px;
  --qty-icon-size: 1rem;
  --qty-pill-width: 160px;
  --qty-pill-padding: 0 8px;
  --qty-pill-font: 0.75rem;
}

/*
 * --large deliberately declares no tokens: it inherits the base scale and is
 * excluded from the mobile shrink at the bottom of this file.
 */

.qty-blocked-pill,
.qty-add-cta {
  justify-content: center;
  box-sizing: border-box;
  font-weight: bold;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: var(--qty-pill-width);
  min-width: var(--qty-pill-width);
  max-width: var(--qty-pill-width);
  height: var(--qty-size);
  padding: var(--qty-pill-padding);
  font-size: var(--qty-pill-font);
  border-radius: var(--qty-radius);
}

.qty-blocked-pill {
  color: var(--product-blocked-text);
  border: 1px solid var(--light-gray);
  background-color: var(--white);
}

.qty-add-cta {
  gap: 0.4rem;
  margin: 0;
  font-family: inherit;
  font-size: 0.8125rem;
  color: var(--dark-blue);
  border: 1px solid var(--dark-blue);
  background-color: var(--white);
  cursor: pointer;
}

.qty-add-cta:hover,
.qty-add-cta:focus-visible {
  border-width: 2px;
  outline: none;
}

.qty-add-cta-icon {
  flex-shrink: 0;
  width: var(--qty-icon-size);
  height: var(--qty-icon-size);
  background-color: var(--dark-blue);
  mask: url('/icons/cart.svg') center / contain no-repeat;
  -webkit-mask: url('/icons/cart.svg') center / contain no-repeat;
}

.qty-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  background-color: transparent;
  color: var(--dark-blue);
  font-weight: bold;
  line-height: 1;
  border: 1px solid var(--dark-blue);
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;
  width: var(--qty-size);
  min-width: var(--qty-size);
  max-width: var(--qty-size);
  height: var(--qty-size);
  padding: 0;
  font-size: var(--qty-btn-font);
  border-radius: var(--qty-radius);
}

/* The icon defaults to 1em, so font-size drives it in both svg and css modes. */
.qty-btn.bin {
  font-size: var(--qty-icon-size);
}

.qty-btn.bin :deep(svg) {
  width: var(--qty-icon-size);
  height: var(--qty-icon-size);
}

.qty-btn:hover:not(:disabled) {
  background-color: var(--dark-blue);
  color: var(--white);
}

.qty-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
  pointer-events: none;
}

.qty-btn--inactive:disabled {
  color: var(--light-gray);
  border-color: var(--light-gray);
  opacity: 1;
  cursor: not-allowed;
}

.qty-btn.add {
  background-color: var(--accent-red);
  color: var(--white);
  border: none;
}

.qty-btn.add:hover:not(:disabled) {
  opacity: 0.85;
}

.qty-btn.add:disabled {
  background-color: var(--light-gray);
  color: var(--white);
  border: none;
  opacity: 1;
  cursor: not-allowed;
}

.qty-value {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-sizing: border-box;
  gap: 3px;
  white-space: nowrap;
  font-weight: bold;
  text-align: center;
  border: 1px solid var(--ligth-blue);
  color: var(--blue);
  background-color: var(--white);
  color-scheme: light;
  font-variant-numeric: tabular-nums;
  /* ch resolves against this rule's own font-size, so the box scales with it. */
  width: calc(2.1ch + 3px + 4.2ch + 6px);
  min-width: calc(2.1ch + 3px + 4.2ch + 6px);
  max-width: calc(2.1ch + 3px + 4.2ch + 6px);
  height: var(--qty-size);
  padding: 0 3px;
  font-size: var(--qty-value-font);
  border-radius: var(--qty-radius);
}

.qty-value-unit {
  font-size: 0.7em;
}

/* --default drops to the compact scale on phones; --large and --compact don't. */
@media (max-width: 768px) {
  .qty-controls--default {
    --qty-size: 34px;
    --qty-gap: 0.4rem;
    --qty-btn-font: 1.25rem;
    --qty-value-font: 1rem;
    --qty-radius: 22px;
    --qty-icon-size: 1.125rem;
    --qty-pill-width: 160px;
    --qty-pill-font: 0.75rem;
  }

  .qty-add-cta.qty-controls--default {
    font-size: 0.6875rem;
  }
}
</style>
